import { useEffect, useMemo } from 'react';
import { useFlag } from '@openfeature/react-sdk';
import { FEATURE_FLAGS } from '../constants/constants';
import useNRLazyQuery from '../graphql/useNRLazyQuery';
import { LIST_MY_ACRS, LIST_MY_ACRS_CURSOR } from '../graphql/queries/rbac';
import { usePermissionCheck } from '../components/Permissions/useHasPermission';
import { RBAC_PERMISSIONS } from '../pages/NRSettings/NRUsersAndAccess/NRRoles/NRCustomRoles/NRCustomRolePermissions.constants';
import { ACR_RESOURCE_TYPE } from '../pages/NRSettings/NRUsersAndAccess/rbac.constants';
import { buildDisplayTags, normalizeUserGrants } from './userAccess.utils';

const EMPTY_NODES = [];
const EMPTY_GRANTS = [];

const RBAC_QUERY_OPTIONS = {
  fetchPolicy: 'cache-and-network'
};

/**
 * Loads the current user's access grants via listMyACRs.
 * Gated by settings-users-and-access; remove skip/flag check when flag is retired.
 */
function useCurrentUserAccess() {
  const { value: isRbacEnabled } = useFlag(FEATURE_FLAGS.SETTINGS_USERS_AND_ACCESS, false);
  const { allowed: canViewAcrs, loading: isAcrsCapabilityLoading } = usePermissionCheck({
    rbacPermission: RBAC_PERMISSIONS.SETTINGS_ADMINISTRATION_VIEW,
    resourceType: ACR_RESOURCE_TYPE.ORGANIZATION,
    loadingValue: false
  });

  const skip = !isRbacEnabled;

  const [acrNodes, acrsLoading, acrsCalled, fetchMyAcrs, , acrsError] = useNRLazyQuery({
    query: LIST_MY_ACRS,
    qlObjKey: 'listMyACRs',
    defaultValue: EMPTY_NODES,
    options: RBAC_QUERY_OPTIONS,
    transformData: data => data?.edges?.map(edge => edge.node) ?? EMPTY_NODES
  });

  useEffect(() => {
    if (skip) return;

    fetchMyAcrs({
      ...RBAC_QUERY_OPTIONS,
      variables: { cursor: LIST_MY_ACRS_CURSOR }
    });
  }, [skip, fetchMyAcrs]);

  const grants = useMemo(() => {
    if (skip) return EMPTY_GRANTS;
    return normalizeUserGrants(acrNodes);
  }, [skip, acrNodes]);

  const displayTags = useMemo(() => buildDisplayTags(grants), [grants]);

  const loading = isRbacEnabled && (!acrsCalled || acrsLoading);

  return {
    grants,
    displayTags,
    called: !skip && acrsCalled,
    loading,
    error: skip ? undefined : acrsError,
    canViewAcrs: isRbacEnabled && canViewAcrs,
    isAcrsCapabilityLoading: isRbacEnabled && isAcrsCapabilityLoading
  };
}

export default useCurrentUserAccess;
